import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Users, UserPlus, Activity, CreditCard, Mail, TrendingUp, Clock } from "lucide-react";
import { useTranslation } from "../translations";
import axios from "axios";
import AdminUsagePanel from "./AdminUsagePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const colorStyles = {
  green: { bg: "bg-green-100", text: "text-green-600" },
  blue: { bg: "bg-blue-100", text: "text-blue-600" },
  purple: { bg: "bg-purple-100", text: "text-purple-600" },
  orange: { bg: "bg-orange-100", text: "text-orange-600" },
};

const StatCard = ({ icon: Icon, title, value, subtitle, color = "green" }) => {
  const styles = colorStyles[color] || colorStyles.green;
  return (
    <Card data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${styles.bg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${styles.text}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-muted-foreground">{title}</p>
            <p className="text-xl sm:text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-[11px] sm:text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ReferralAdminCard = () => {
  const [ov, setOv] = useState(null);

  useEffect(() => {
    axios.get(`${API}/referral/admin/overview`).then((r) => setOv(r.data)).catch(() => {});
  }, []);

  if (!ov) return null;
  const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

  return (
    <Card data-testid="referral-admin-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          🎁 Indique um Amigo — Oferta de Fundador
          <Badge variant="outline" className={`text-[10px] ${ov.program?.active ? 'text-green-600 border-green-300' : 'text-red-500 border-red-300'}`}>
            {ov.program?.active ? 'Ativa' : 'Encerrada'} · {ov.program?.paid_count}/{ov.program?.max_paid_subscribers} assinantes
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <div className="bg-amber-50 rounded-lg p-2">
            <p className="text-xl font-bold text-amber-700" data-testid="referral-admin-cadastrados">{ov.indicacoes?.total ?? 0}</p>
            <p className="text-[11px] text-gray-500">Cadastrados c/ código</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2">
            <p className="text-xl font-bold text-green-700" data-testid="referral-admin-convertidos">{ov.indicacoes?.convertidas ?? 0}</p>
            <p className="text-[11px] text-gray-500">Assinaram</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-2">
            <p className="text-xl font-bold text-blue-700">{ov.indicacoes?.bonus_pendentes ?? 0}</p>
            <p className="text-[11px] text-gray-500">Bônus pendentes</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-xl font-bold text-gray-700">{ov.indicacoes?.bonus_usados ?? 0}</p>
            <p className="text-[11px] text-gray-500">Bônus usados</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Mês: receita cheia {fmt(ov.financeiro_mes?.receita_cheia)} · descontos {fmt(ov.financeiro_mes?.descontos_concedidos)} · líquida <strong>{fmt(ov.financeiro_mes?.receita_liquida)}</strong> · de indicados {fmt(ov.financeiro_mes?.receita_de_indicados)}
        </p>
      </CardContent>
    </Card>
  );
};

const AdminStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="stats-loading">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <Card key={i}><CardContent className="p-6"><div className="h-16 bg-muted rounded animate-pulse" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return <p className="text-center text-muted-foreground py-8">{t('errorLoadingStats') || 'Erro ao carregar estatísticas'}</p>;
  }

  const userTypeLabels = {
    professional: t('professional') || 'Profissional',
    employer: t('employer') || 'Proprietário',
    admin: 'Admin'
  };

  return (
    <div className="space-y-6" data-testid="admin-stats-panel">
      <h2 className="text-xl font-bold flex items-center gap-2">
        <TrendingUp className="w-5 h-5" />
        {t('statsTitle') || 'Estatísticas da Plataforma'}
      </h2>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={Users}
          title={t('statsTotal') || "Total de Usuários"}
          value={stats.total_users}
          subtitle={`+${stats.new_users_30d} ${t('statsLast30d') || 'últimos 30 dias'}`}
          color="green"
        />
        <StatCard
          icon={Activity}
          title={t('statsActive') || "Usuários Ativos"}
          value={stats.active_users_30d}
          subtitle={`${stats.active_users_7d} ${t('statsLast7d') || 'últimos 7 dias'}`}
          color="blue"
        />
        <StatCard
          icon={CreditCard}
          title={t('statsSubscriptions') || "Assinaturas Ativas"}
          value={stats.subscriptions.active + stats.subscriptions.trial}
          subtitle={`${stats.subscriptions.trial} ${t('statsTrial') || 'em teste'}`}
          color="purple"
        />
        <StatCard
          icon={Mail}
          title={t('statsMessages') || "Mensagens"}
          value={stats.messages.total}
          subtitle={`${stats.messages.unread} ${t('statsUnread') || 'não lidas'}`}
          color="orange"
        />
      </div>

      {/* Growth & Distribution */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Users by Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('statsUsersByType') || 'Usuários por Tipo'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.users_by_type).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      type === 'professional' ? 'bg-green-500' : 
                      type === 'employer' ? 'bg-blue-500' : 'bg-purple-500'
                    }`} />
                    <span className="text-sm">{userTypeLabels[type] || type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{count}</span>
                    <span className="text-xs text-muted-foreground">
                      ({stats.total_users > 0 ? Math.round((count / stats.total_users) * 100) : 0}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* New Users */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('statsGrowth') || 'Crescimento'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-green-600" />
                  <span className="text-sm">{t('statsNew7d') || 'Novos (7 dias)'}</span>
                </div>
                <Badge className="bg-green-100 text-green-800">+{stats.new_users_7d}</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">{t('statsNew30d') || 'Novos (30 dias)'}</span>
                </div>
                <Badge className="bg-blue-100 text-blue-800">+{stats.new_users_30d}</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-600" />
                  <span className="text-sm">{t('statsActive7d') || 'Ativos (7 dias)'}</span>
                </div>
                <Badge className="bg-purple-100 text-purple-800">{stats.active_users_7d}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Signups */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t('statsRecentUsers') || 'Últimos Cadastros'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats.recent_users.map((u, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.name || 'Sem nome'}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <Badge variant="outline" className="text-[10px]">
                    {userTypeLabels[u.user_type] || u.user_type}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}
                  </span>
                </div>
              </div>
            ))}
            {stats.recent_users.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('statsNoUsers') || 'Nenhum usuário cadastrado ainda.'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Indique um Amigo — visão do admin */}
      <ReferralAdminCard />

      {/* Uso da Plataforma */}
      <AdminUsagePanel />
    </div>
  );
};

export default AdminStatsPanel;
